import { supabase } from '../lib/supabaseClient';
import {
  compareOpportunitiesByPriority,
  type Opportunity,
  type OpportunityCreateInput,
  type OpportunityLevel,
  type OpportunitySourceKind,
  type OpportunityStatus,
  type OpportunityUpdateInput,
} from '../types/opportunity';
import { buildOpportunityActionOutcome, buildOpportunityInsert } from '../utils/problemOpportunity';

export interface OpportunityFilters {
  status?: OpportunityStatus;
  impact_level?: OpportunityLevel;
  effort_level?: OpportunityLevel;
  source_kind?: OpportunitySourceKind;
  owner_id?: string;
  search?: string;
}

function asContext(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function mapRow(row: Record<string, unknown>, ownerName: string | null = null): Opportunity {
  return {
    id: String(row.id),
    organization_id: String(row.organization_id),
    title: String(row.title ?? ''),
    description: row.description == null ? null : String(row.description),
    status: row.status as OpportunityStatus,
    category: String(row.category ?? 'other'),
    impact_level: (row.impact_level as OpportunityLevel | null) ?? null,
    effort_level: (row.effort_level as OpportunityLevel | null) ?? null,
    source_kind: row.source_kind as OpportunitySourceKind,
    source_issue_id: row.source_issue_id == null ? null : String(row.source_issue_id),
    source_indicator_id: row.source_indicator_id == null ? null : String(row.source_indicator_id),
    source_project_id: row.source_project_id == null ? null : String(row.source_project_id),
    source_ref_id: row.source_ref_id == null ? null : String(row.source_ref_id),
    source_label: row.source_label == null ? null : String(row.source_label),
    source_context: asContext(row.source_context),
    owner_id: row.owner_id == null ? null : String(row.owner_id),
    created_by: row.created_by == null ? null : String(row.created_by),
    created_at: String(row.created_at),
    updated_at: String(row.updated_at),
    owner_name: ownerName,
  };
}

async function withOwnerNames(rows: Opportunity[]): Promise<Opportunity[]> {
  const userIds = [...new Set(rows.map((row) => row.owner_id).filter(Boolean))] as string[];
  if (userIds.length === 0) return rows;

  const { data: profiles } = await supabase
    .from('user_profiles')
    .select('user_id, name')
    .in('user_id', userIds);

  const names = Object.fromEntries(
    (profiles ?? []).map((profile: { user_id: string; name?: string | null }) => [profile.user_id, profile.name ?? null]),
  );

  return rows.map((row) => ({
    ...row,
    owner_name: row.owner_id ? names[row.owner_id] ?? null : null,
  }));
}

function searchClause(search: string): string | null {
  const term = search.replace(/[%_,*()"\\]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!term) return null;
  const pattern = `"%${term}%"`;
  return `title.ilike.${pattern},description.ilike.${pattern}`;
}

async function resolveUserId(explicit?: string | null): Promise<string | null> {
  if (explicit) return explicit;
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user?.id ?? null;
}

export class OpportunityService {
  static async list(organizationId: string, filters?: OpportunityFilters): Promise<Opportunity[]> {
    let query = supabase
      .from('opportunities')
      .select('*')
      .eq('organization_id', organizationId);

    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.impact_level) query = query.eq('impact_level', filters.impact_level);
    if (filters?.effort_level) query = query.eq('effort_level', filters.effort_level);
    if (filters?.source_kind) query = query.eq('source_kind', filters.source_kind);
    if (filters?.owner_id) query = query.eq('owner_id', filters.owner_id);
    const clause = filters?.search ? searchClause(filters.search) : null;
    if (clause) query = query.or(clause);

    const { data, error } = await query;
    if (error) {
      console.error('[OpportunityService.list]', error.message);
      throw new Error('Não foi possível carregar as oportunidades.');
    }

    const rows = ((data ?? []) as Record<string, unknown>[]).map((row) => mapRow(row));
    const enriched = await withOwnerNames(rows);
    return enriched.sort(compareOpportunitiesByPriority);
  }

  static async getById(organizationId: string, opportunityId: string): Promise<Opportunity | null> {
    const { data, error } = await supabase
      .from('opportunities')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('id', opportunityId)
      .maybeSingle();

    if (error) {
      console.error('[OpportunityService.getById]', error.message);
      throw new Error('Não foi possível abrir a oportunidade.');
    }
    if (!data) return null;
    const [enriched] = await withOwnerNames([mapRow(data as Record<string, unknown>)]);
    return enriched;
  }

  static async create(organizationId: string, input: OpportunityCreateInput): Promise<Opportunity> {
    const title = input.title.trim();
    if (!title) throw new Error('Informe o título da oportunidade.');

    const createdBy = await resolveUserId(input.created_by);
    const payload = buildOpportunityInsert(organizationId, { ...input, title }, createdBy);

    const { data, error } = await supabase
      .from('opportunities')
      .insert(payload)
      .select('*')
      .single();

    if (error || !data) {
      console.error('[OpportunityService.create]', error?.message);
      throw new Error('Não foi possível capturar a oportunidade.');
    }

    const [enriched] = await withOwnerNames([mapRow(data as Record<string, unknown>)]);
    return enriched;
  }

  static async listByIssue(organizationId: string, issueId: string): Promise<Opportunity[]> {
    const { data, error } = await supabase
      .from('opportunities')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('source_kind', 'problem')
      .eq('source_issue_id', issueId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[OpportunityService.listByIssue]', error.message);
      throw new Error('Não foi possível carregar as oportunidades do problema.');
    }

    const rows = ((data ?? []) as Record<string, unknown>[]).map((row) => mapRow(row));
    return withOwnerNames(rows);
  }

  static async update(opportunityId: string, input: OpportunityUpdateInput): Promise<Opportunity> {
    const patch: Record<string, unknown> = {};
    if (input.title !== undefined) {
      const title = input.title.trim();
      if (!title) throw new Error('Informe o título da oportunidade.');
      patch.title = title;
    }
    if (input.description !== undefined) patch.description = input.description?.trim() || null;
    if (input.category !== undefined) patch.category = input.category.trim() || 'other';
    if (input.impact_level !== undefined) patch.impact_level = input.impact_level;
    if (input.effort_level !== undefined) patch.effort_level = input.effort_level;
    if (input.owner_id !== undefined) patch.owner_id = input.owner_id || null;
    if (input.status !== undefined) patch.status = input.status;

    const { data, error } = await supabase
      .from('opportunities')
      .update(patch)
      .eq('id', opportunityId)
      .select('*')
      .single();

    if (error || !data) {
      console.error('[OpportunityService.update]', error?.message);
      throw new Error('Não foi possível atualizar a oportunidade.');
    }

    const [enriched] = await withOwnerNames([mapRow(data as Record<string, unknown>)]);
    return enriched;
  }

  static async delete(organizationId: string, opportunityId: string): Promise<void> {
    const { data, error } = await supabase
      .from('opportunities')
      .delete()
      .eq('organization_id', organizationId)
      .eq('id', opportunityId)
      .select('id');

    if (error) {
      console.error('[OpportunityService.delete]', error.message);
      throw new Error('Não foi possível remover a oportunidade.');
    }
    if (!data || data.length === 0) {
      throw new Error('Não foi possível remover a oportunidade.');
    }
  }

  static async listOutcomeCounts(
    organizationId: string,
    opportunityIds: string[],
  ): Promise<OpportunityOutcomeRef[]> {
    if (!organizationId || opportunityIds.length === 0) return [];
    const { data, error } = await supabase
      .from('opportunity_outcomes')
      .select('opportunity_id, action_id, kaizen_id, project_id')
      .in('opportunity_id', opportunityIds);

    if (error) {
      console.error('[OpportunityService.listOutcomeCounts]', error.message);
      throw new Error('Não foi possível contar os desdobramentos.');
    }

    return ((data ?? []) as Record<string, unknown>[]).map(mapOutcomeRef);
  }

  static async listOutcomes(
    organizationId: string,
    opportunityId: string,
  ): Promise<OpportunityOutcomeDetail[]> {
    const { data: owned, error: ownedError } = await supabase
      .from('opportunities')
      .select('id')
      .eq('organization_id', organizationId)
      .eq('id', opportunityId)
      .maybeSingle();

    if (ownedError) {
      console.error('[OpportunityService.listOutcomes]', ownedError.message);
      throw new Error('Não foi possível carregar os desdobramentos.');
    }
    if (!owned) return [];

    const { data, error } = await supabase
      .from('opportunity_outcomes')
      .select('id, opportunity_id, action_id, kaizen_id, project_id, created_at')
      .eq('opportunity_id', opportunityId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[OpportunityService.listOutcomes]', error.message);
      throw new Error('Não foi possível carregar os desdobramentos.');
    }

    const rows = ((data ?? []) as Record<string, unknown>[]).map(mapOutcomeRow);
    return enrichOutcomes(rows);
  }

  static async linkAction(opportunityId: string, actionId: string): Promise<void> {
    const createdBy = await resolveUserId();
    const { error } = await supabase
      .from('opportunity_outcomes')
      .insert(buildOpportunityActionOutcome(opportunityId, actionId, createdBy));

    if (!error) return;
    if (error.code === '23505') return;

    console.error('[OpportunityService.linkAction]', error.message);
    throw new Error('Não foi possível vincular a ação.');
  }

  static async linkProject(opportunityId: string, projectId: string): Promise<void> {
    const createdBy = await resolveUserId();
    const { error } = await supabase
      .from('opportunity_outcomes')
      .insert({
        opportunity_id: opportunityId,
        project_id: projectId,
        created_by: createdBy,
      });

    if (!error) return;
    if (error.code === '23505') return;

    console.error('[OpportunityService.linkProject]', error.message);
    throw new Error('Não foi possível vincular o projeto.');
  }

  static async linkKaizen(opportunityId: string, kaizenId: string): Promise<void> {
    const createdBy = await resolveUserId();
    const { error } = await supabase
      .from('opportunity_outcomes')
      .insert({
        opportunity_id: opportunityId,
        kaizen_id: kaizenId,
        created_by: createdBy,
      });

    if (!error) return;
    if (error.code === '23505') return;

    console.error('[OpportunityService.linkKaizen]', error.message);
    throw new Error('Não foi possível vincular o Kaizen.');
  }

  static async unlinkOutcome(outcomeId: string): Promise<void> {
    const { data, error } = await supabase
      .from('opportunity_outcomes')
      .delete()
      .eq('id', outcomeId)
      .select('id');

    if (error) {
      console.error('[OpportunityService.unlinkOutcome]', error.message);
      throw new Error('Não foi possível remover o vínculo.');
    }
    if (!data || data.length === 0) {
      throw new Error('Não foi possível remover o vínculo.');
    }
  }
}

export interface OpportunityOutcomeRef {
  opportunity_id: string;
  action_id: string | null;
  kaizen_id: string | null;
  project_id: string | null;
}

export type OpportunityOutcomeKind = 'action' | 'kaizen' | 'project';

export interface OpportunityOutcomeDetail {
  id: string;
  opportunity_id: string;
  kind: OpportunityOutcomeKind;
  target_id: string;
  accessible: boolean;
  title: string | null;
  status: string | null;
  owner_label: string | null;
}

function mapOutcomeRef(row: Record<string, unknown>): OpportunityOutcomeRef {
  return {
    opportunity_id: String(row.opportunity_id),
    action_id: row.action_id == null ? null : String(row.action_id),
    kaizen_id: row.kaizen_id == null ? null : String(row.kaizen_id),
    project_id: row.project_id == null ? null : String(row.project_id),
  };
}

function mapOutcomeRow(row: Record<string, unknown>): OpportunityOutcomeRef & { id: string } {
  return {
    id: String(row.id),
    ...mapOutcomeRef(row),
  };
}

async function profileNames(userIds: string[]): Promise<Record<string, string | null>> {
  if (userIds.length === 0) return {};
  const { data } = await supabase
    .from('user_profiles')
    .select('user_id, name')
    .in('user_id', userIds);
  return Object.fromEntries(
    (data ?? []).map((profile: { user_id: string; name?: string | null }) => [profile.user_id, profile.name ?? null]),
  );
}

async function enrichOutcomes(
  rows: Array<OpportunityOutcomeRef & { id: string }>,
): Promise<OpportunityOutcomeDetail[]> {
  const actionIds = rows.map((row) => row.action_id).filter(Boolean) as string[];
  const kaizenIds = rows.map((row) => row.kaizen_id).filter(Boolean) as string[];
  const projectIds = rows.map((row) => row.project_id).filter(Boolean) as string[];

  const [actionsResult, kaizenResult, projectResult] = await Promise.all([
    actionIds.length
      ? supabase.from('actions').select('id, title, status, assigned_to').in('id', actionIds)
      : Promise.resolve({ data: [], error: null }),
    kaizenIds.length
      ? supabase.from('kaizen_records').select('id, title, status, responsible').in('id', kaizenIds)
      : Promise.resolve({ data: [], error: null }),
    projectIds.length
      ? supabase.from('projects').select('id, name, status, owner_id').in('id', projectIds)
      : Promise.resolve({ data: [], error: null }),
  ]);

  if (actionsResult.error || kaizenResult.error || projectResult.error) {
    throw new Error('Não foi possível carregar os desdobramentos.');
  }

  const actions = new Map(
    ((actionsResult.data ?? []) as Array<{ id: string; title?: string; status?: string; assigned_to?: string | null }>)
      .map((row) => [row.id, row]),
  );
  const kaizens = new Map(
    ((kaizenResult.data ?? []) as Array<{ id: string; title?: string; status?: string; responsible?: string | null }>)
      .map((row) => [row.id, row]),
  );
  const projects = new Map(
    ((projectResult.data ?? []) as Array<{ id: string; name?: string; status?: string; owner_id?: string | null }>)
      .map((row) => [row.id, row]),
  );

  const names = await profileNames([
    ...[...actions.values()].map((row) => row.assigned_to).filter(Boolean) as string[],
    ...[...projects.values()].map((row) => row.owner_id).filter(Boolean) as string[],
  ]);

  return rows.flatMap((row): OpportunityOutcomeDetail[] => {
    if (row.action_id) {
      const action = actions.get(row.action_id);
      return [{
        id: row.id,
        opportunity_id: row.opportunity_id,
        kind: 'action' as const,
        target_id: row.action_id,
        accessible: Boolean(action),
        title: action?.title ?? null,
        status: action?.status ?? null,
        owner_label: action?.assigned_to ? names[action.assigned_to] ?? null : null,
      }];
    }
    if (row.kaizen_id) {
      const kaizen = kaizens.get(row.kaizen_id);
      return [{
        id: row.id,
        opportunity_id: row.opportunity_id,
        kind: 'kaizen' as const,
        target_id: row.kaizen_id,
        accessible: Boolean(kaizen),
        title: kaizen?.title ?? null,
        status: kaizen?.status ?? null,
        owner_label: kaizen?.responsible ?? null,
      }];
    }
    if (row.project_id) {
      const project = projects.get(row.project_id);
      return [{
        id: row.id,
        opportunity_id: row.opportunity_id,
        kind: 'project' as const,
        target_id: row.project_id,
        accessible: Boolean(project),
        title: project?.name ?? null,
        status: project?.status ?? null,
        owner_label: project?.owner_id ? names[project.owner_id] ?? null : null,
      }];
    }
    return [];
  });
}

export function formatOutcomeSummary(rows: OpportunityOutcomeRef[]): string {
  const actions = rows.filter((row) => row.action_id).length;
  const kaizens = rows.filter((row) => row.kaizen_id).length;
  const projects = rows.filter((row) => row.project_id).length;
  const parts: string[] = [];
  if (actions === 1) parts.push('1 Ação');
  else if (actions > 1) parts.push(`${actions} Ações`);
  if (kaizens === 1) parts.push('1 Kaizen');
  else if (kaizens > 1) parts.push(`${kaizens} Kaizens`);
  if (projects === 1) parts.push('1 Projeto');
  else if (projects > 1) parts.push(`${projects} Projetos`);
  return parts.join(' + ');
}
